import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CustomButton from '../../components/mui/CustomButton';
import OTPInput from 'react-otp-input';
import { useDispatch } from 'react-redux';
import { login, verifyOtp } from '../../actions/userAuth';
import { clearAuthorization, setXsrfToken } from '../../utils/api';
import { enqueueSnackbar } from 'notistack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.06); }
`;

const PageRoot = styled(Box)({
  minHeight: '100vh',
  position: 'relative',
  overflow: 'hidden',
  background:
    'radial-gradient(ellipse 90% 70% at 0% 0%, rgba(17, 64, 155, 0.4) 0%, transparent 50%), radial-gradient(ellipse 80% 60% at 100% 100%, rgba(56, 189, 248, 0.15) 0%, transparent 45%), linear-gradient(160deg, #030508 0%, #070b14 35%, #0a1020 70%, #060a12 100%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundImage:
      'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
    backgroundSize: '56px 56px',
    maskImage:
      'radial-gradient(ellipse 80% 70% at 50% 30%, black 15%, transparent 70%)',
    pointerEvents: 'none',
  },
});

const GlowOrb = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'orbColor' && prop !== 'orbSize',
})(({ orbColor, orbSize, top, left, right, bottom }) => ({
  position: 'absolute',
  top,
  left,
  right,
  bottom,
  width: orbSize,
  height: orbSize,
  borderRadius: '50%',
  background: orbColor,
  filter: 'blur(90px)',
  animation: `${glowPulse} 10s ease-in-out infinite`,
  pointerEvents: 'none',
}));

const GlassCard = styled(Box)({
  position: 'relative',
  padding: '44px 40px',
  borderRadius: '28px',
  background:
    'linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(28px)',
  WebkitBackdropFilter: 'blur(28px)',
  boxShadow:
    '0 32px 96px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255,255,255,0.04) inset',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '10%',
    right: '10%',
    height: '1px',
    background:
      'linear-gradient(90deg, transparent, rgba(125, 211, 252, 0.6), transparent)',
  },
});

const HeroPanel = styled(Box)({
  position: 'relative',
  borderRadius: '32px',
  overflow: 'hidden',
  minHeight: 'calc(100vh - 100px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 40px 80px rgba(0, 0, 0, 0.55)',
});

const FeaturePill = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  borderRadius: '14px',
  background: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(12px)',
  color: '#f1f5f9',
  fontSize: 13,
  fontWeight: 500,
});

const darkTextFieldSx = {
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    '&.Mui-focused': { color: '#93c5fd' },
  },
  '& .MuiOutlinedInput-root': {
    color: '#f8fafc',
    borderRadius: '14px',
    fontSize: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    transition: 'all 0.25s ease',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(147, 197, 253, 0.5)' },
    '&.Mui-focused fieldset': {
      borderColor: '#93c5fd',
      borderWidth: 1,
    },
    '&.Mui-focused': {
      boxShadow: '0 0 0 4px rgba(147, 197, 253, 0.12)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      '& input': { WebkitTextFillColor: 'rgba(255,255,255,0.45)' },
    },
  },
  '& .MuiFormHelperText-root.Mui-error': { color: '#fca5a5' },
};

const OTP_LENGTH = 6;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_IMAGE =
  'https://plus.unsplash.com/premium_photo-1728670001843-8233c4371d13?w=1200&auto=format&fit=crop&q=80';

export const Login = () => {
  const [mobile, setMobile] = useState('');
  const [isMobileValid, setIsMobileValid] = useState(true);
  const [otp, setOtp] = useState('');
  const [otpInputActive, setOtpInputActive] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sendOtpLoading, setSendOtpLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const validateMobile = (value) => /^\+?[1-9]\d{9,14}$/.test(value);

  const handleMobileChange = (e) => {
    const enteredMobile = e.target.value;
    setMobile(enteredMobile);
    setIsMobileValid(enteredMobile ? validateMobile(enteredMobile) : true);
  };

  useEffect(() => {
    if (localStorage.getItem('token')) {
      navigate('/dashboard');
      return;
    }
    clearAuthorization();
  }, [navigate]);

  const handleContinue = (e) => {
    e.preventDefault();
    if (!isMobileValid) return;
    setSendOtpLoading(true);
    dispatch(
      login(
        { mobile },
        () => {
          enqueueSnackbar('Otp sent successfully', { variant: 'success' });
          setOtpInputActive(true);
          setSendOtpLoading(false);
        },
        (err) => {
          console.error('Send OTP error:', err?.response || err);
          enqueueSnackbar(
            err?.friendlyMessage || "You don't have access to this portal",
            { variant: 'error' }
          );
          setSendOtpLoading(false);
        }
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoginLoading(true);
    dispatch(
      verifyOtp(
        { mobile, otp },
        (data) => {
          if (!data?.isSuccess) {
            enqueueSnackbar(
              data?.message || "You don't have access to this portal",
              { variant: 'error' }
            );
            setLoginLoading(false);
            return;
          }

          const { authResponse, csrfToken } = data;

          localStorage.setItem('token', authResponse?.token || '');
          localStorage.setItem('SiriAdminUserRole', authResponse?.role || '');
          localStorage.setItem('User id', authResponse?.id || '');
          if (csrfToken) {
            setXsrfToken(csrfToken);
          }
          dispatch({ type: 'LOG_IN', payload: authResponse });
          enqueueSnackbar('Logged in successfully', { variant: 'success' });
          navigate('/dashboard');
          setLoginLoading(false);
        },
        (err) => {
          console.error(
            'Verify OTP error:',
            err?.response?.data?.message || err
          );
          enqueueSnackbar(
            err?.friendlyMessage || "You don't have access to this portal",
            { variant: 'error' }
          );
          setLoginLoading(false);
        }
      )
    );
  };

  const handleBackToPhone = () => {
    setOtpInputActive(false);
    setOtp('');
  };

  const getOtpInputStyle = (active) => ({
    width: '100%',
    maxWidth: '48px',
    height: '54px',
    margin: '0 4px',
    fontSize: '18px',
    fontWeight: 700,
    borderRadius: '14px',
    border: active
      ? '1px solid rgba(147, 197, 253, 0.35)'
      : '1px solid rgba(255, 255, 255, 0.1)',
    background: active ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 255, 255, 0.04)',
    color: '#f8fafc',
    textAlign: 'center',
    outline: 'none',
    transition: 'all 0.2s ease',
    opacity: active ? 1 : 0.35,
    cursor: active ? 'text' : 'not-allowed',
  });

  const premiumButtonSx = {
    height: '54px',
    borderRadius: '14px',
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'none',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    background:
      'linear-gradient(135deg, #0c3483 0%, #11409B 25%, #2563eb 60%, #38bdf8 100%)',
    backgroundSize: '200% auto',
    boxShadow:
      '0 4px 24px rgba(37, 99, 235, 0.45), inset 0 1px 0 rgba(255,255,255,0.2)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 8px 36px rgba(37, 99, 235, 0.55)',
      backgroundPosition: 'right center',
    },
    '&:active': { transform: 'translateY(0)' },
    '&:disabled': {
      background: 'rgba(255, 255, 255, 0.06)',
      color: 'rgba(255, 255, 255, 0.3)',
      boxShadow: 'none',
      transform: 'none',
    },
    '& .MuiCircularProgress-root': { color: '#fff' },
  };

  const steps = [
    { label: 'Phone', done: otpInputActive, active: !otpInputActive },
    { label: 'Verify', done: false, active: otpInputActive },
  ];

  return (
    <PageRoot>
      <GlowOrb top="5%" left="-10%" orbSize={360} orbColor="rgba(17, 64, 155, 0.4)" />
      <GlowOrb
        bottom="0"
        right="-8%"
        orbSize={300}
        orbColor="rgba(56, 189, 248, 0.22)"
        sx={{ animationDelay: '3s' }}
      />

      <Container
        maxWidth="xl"
        sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}
      >
        <Box
          pt={{ xs: 3, md: 4 }}
          pb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box
            sx={{
              bgcolor: '#ffffff',
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            }}
          >
            <Box
              component="img"
              src="https://siripay.co/logo.png"
              alt="SiriPay"
              onError={(e) => {
                e.currentTarget.src = '/logo.png';
              }}
              sx={{
                height: { xs: 26, md: 34 },
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.1)',
              bgcolor: 'rgba(255,255,255,0.04)',
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: '#4ade80',
                boxShadow: '0 0 12px #4ade80',
              }}
            />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Systems operational
            </Typography>
          </Box>
        </Box>

        <Grid
          container
          spacing={{ xs: 3, md: 5 }}
          alignItems="center"
          sx={{ minHeight: { xs: 'auto', md: 'calc(100vh - 110px)' }, pb: 6 }}
        >
          <Grid item xs={12} md={5} lg={5}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
            >
              <GlassCard>
                <Stack direction="row" spacing={1} mb={3}>
                  {steps.map((step, i) => (
                    <Box key={step.label} sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          mb: 1,
                          bgcolor: step.done || step.active
                            ? 'linear-gradient(90deg, #2563eb, #38bdf8)'
                            : 'rgba(255,255,255,0.1)',
                          background: step.done || step.active
                            ? 'linear-gradient(90deg, #2563eb, #38bdf8)'
                            : 'rgba(255,255,255,0.1)',
                          boxShadow: step.active
                            ? '0 0 12px rgba(56, 189, 248, 0.5)'
                            : 'none',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: step.active
                            ? '#93c5fd'
                            : 'rgba(255,255,255,0.35)',
                          fontWeight: step.active ? 600 : 400,
                        }}
                      >
                        {i + 1}. {step.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.75,
                    py: 0.85,
                    mb: 2.5,
                    borderRadius: '999px',
                    bgcolor: alpha('#38bdf8', 0.1),
                    border: '1px solid rgba(56, 189, 248, 0.25)',
                  }}
                >
                  <LockOutlinedIcon sx={{ fontSize: 15, color: '#7dd3fc' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#bae6fd',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      fontSize: 11,
                    }}
                  >
                    ADMIN ACCESS
                  </Typography>
                </Box>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={otpInputActive ? 'otp' : 'phone'}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 12 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.75rem', md: '2rem' },
                        lineHeight: 1.15,
                        letterSpacing: '-0.03em',
                        background:
                          'linear-gradient(135deg, #ffffff 0%, #cbd5e1 50%, #94a3b8 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {otpInputActive
                        ? 'Check your phone'
                        : 'Sign in to SiriPay'}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.55)',
                        mt: 1.5,
                        mb: 3,
                        lineHeight: 1.65,
                        maxWidth: 380,
                      }}
                    >
                      {otpInputActive
                        ? `We sent a 6-digit code to ${mobile}. Enter it below to continue.`
                        : 'Enter your registered mobile number. Only authorized admins can access this portal.'}
                    </Typography>
                  </motion.div>
                </AnimatePresence>

                <form onSubmit={otpInputActive ? handleSubmit : handleContinue}>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth
                      label="Mobile number"
                      placeholder="+91 98765 43210"
                      value={mobile}
                      onChange={handleMobileChange}
                      disabled={otpInputActive}
                      error={!isMobileValid}
                      helperText={
                        !isMobileValid
                          ? 'Include country code (e.g. +973631670013)'
                          : ' '
                      }
                      InputProps={{
                        startAdornment: (
                          <PhoneAndroidOutlinedIcon
                            sx={{
                              mr: 1.25,
                              color: 'rgba(255,255,255,0.35)',
                              fontSize: 22,
                            }}
                          />
                        ),
                      }}
                      sx={darkTextFieldSx}
                    />

                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: '18px',
                        bgcolor: 'rgba(0,0,0,0.2)',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: otpInputActive
                              ? 'rgba(255,255,255,0.9)'
                              : 'rgba(255,255,255,0.4)',
                            fontWeight: 600,
                          }}
                        >
                          Verification code
                        </Typography>
                        {otpInputActive && (
                          <Typography
                            component="button"
                            type="button"
                            onClick={handleBackToPhone}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              color: '#93c5fd',
                              fontSize: 13,
                              fontWeight: 500,
                              '&:hover': { color: '#bfdbfe' },
                            }}
                          >
                            <ArrowBackIosNewIcon sx={{ fontSize: 11 }} />
                            Edit number
                          </Typography>
                        )}
                      </Stack>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          '& > div': { width: '100%', justifyContent: 'center' },
                        }}
                      >
                        <OTPInput
                          value={otp}
                          onChange={setOtp}
                          numInputs={OTP_LENGTH}
                          inputType="tel"
                          shouldAutoFocus={otpInputActive}
                          renderSeparator={<span style={{ width: 4 }} />}
                          renderInput={(props) => (
                            <input
                              {...props}
                              style={getOtpInputStyle(otpInputActive)}
                              disabled={!otpInputActive}
                              onFocus={(e) => {
                                if (otpInputActive) {
                                  e.target.style.borderColor = '#93c5fd';
                                  e.target.style.boxShadow =
                                    '0 0 0 4px rgba(147, 197, 253, 0.15)';
                                }
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor =
                                  'rgba(147, 197, 253, 0.35)';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Box>

                    <CustomButton
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={
                        otpInputActive
                          ? otp.length !== OTP_LENGTH || loginLoading
                          : !validateMobile(mobile) ||
                            !mobile ||
                            sendOtpLoading
                      }
                      sx={premiumButtonSx}
                    >
                      {otpInputActive ? (
                        loginLoading ? (
                          <CircularProgress size={22} />
                        ) : (
                          'Continue to dashboard'
                        )
                      ) : sendOtpLoading ? (
                        <CircularProgress size={22} />
                      ) : (
                        'Send verification code'
                      )}
                    </CustomButton>
                  </Stack>
                </form>

                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  mt={3.5}
                  pt={2.5}
                  sx={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ShieldOutlinedIcon
                      sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.35)' }}
                    >
                      256-bit encrypted
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <VerifiedUserOutlinedIcon
                      sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.35)' }}
                    >
                      Admin only
                    </Typography>
                  </Stack>
                </Stack>
              </GlassCard>
            </motion.div>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            lg={7}
            sx={{ display: { xs: 'none', md: 'block' } }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <HeroPanel>
                <Box
                  component="img"
                  src={HERO_IMAGE}
                  alt="SiriPay"
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 'calc(100vh - 100px)',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background:
                      'linear-gradient(180deg, rgba(3,5,8,0.15) 0%, rgba(3,5,8,0.5) 45%, rgba(3,5,8,0.92) 100%), linear-gradient(135deg, rgba(17,64,155,0.35) 0%, transparent 60%)',
                  }}
                />

                <Box
                  sx={{
                    position: 'absolute',
                    top: 32,
                    left: 32,
                    right: 32,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    zIndex: 2,
                  }}
                >
                  <FeaturePill>
                    <SpeedOutlinedIcon sx={{ fontSize: 18, color: '#7dd3fc' }} />
                    Real-time transactions
                  </FeaturePill>
                  <FeaturePill>
                    <InsightsOutlinedIcon sx={{ fontSize: 18, color: '#a78bfa' }} />
                    Smart analytics
                  </FeaturePill>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 40,
                    left: 40,
                    right: 40,
                    zIndex: 2,
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: { md: '2.25rem', lg: '2.75rem' },
                      letterSpacing: '-0.04em',
                      lineHeight: 1.1,
                      maxWidth: 520,
                      textShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    }}
                  >
                    The control center for your payment operations
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.75)',
                      mt: 2,
                      maxWidth: 440,
                      lineHeight: 1.75,
                      fontSize: '1.05rem',
                    }}
                  >
                    Monitor transactions, manage users, and run your business —
                    securely, from one premium dashboard.
                  </Typography>

                  <Stack direction="row" spacing={3} mt={4}>
                    {[
                      { value: '99.9%', label: 'Uptime' },
                      { value: '24/7', label: 'Monitoring' },
                      { value: 'Bank-grade', label: 'Security' },
                    ].map((stat) => (
                      <Box key={stat.label}>
                        <Typography
                          sx={{
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '1.25rem',
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box
                  sx={{
                    position: 'absolute',
                    top: '40%',
                    right: 48,
                    p: 2.5,
                    borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(16px)',
                    animation: `${float} 5s ease-in-out infinite`,
                    zIndex: 2,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.6)' }}
                  >
                    Live volume
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4ade80',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      mt: 0.5,
                    }}
                  >
                    ↑ 12.4%
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.45)' }}
                  >
                    vs last week
                  </Typography>
                </Box>
              </HeroPanel>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </PageRoot>
  );
};
